import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./EmailBuilder.css";
import asharaSocialNetworks from "../../data/AsharaSocialNetworks.json";
import Button from "../../components/Button/Button";
import Popup from "../../components/Popup/Popup";
import { isSafeUrl } from "../../utils/url";
import { sendNewsletter, sendTestNewsletter, resetSendStatus, fetchTopics } from "../../redux/newsletterSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBold, faItalic, faStrikethrough,
  faAlignLeft, faAlignCenter, faAlignRight, faAlignJustify,
  faLink, faImage,
  faListUl, faListOl, faQuoteLeft, faCode, faMinus,
  faPaperPlane, faTrash, faVial,
} from "@fortawesome/free-solid-svg-icons";

const getAsharaSocialUrl = (name) =>
  asharaSocialNetworks.socialNetworks.find((social) => social.name.toLowerCase() === name.toLowerCase())?.url || "";

const FOOTER_SETTINGS = {
  facebookUrl: getAsharaSocialUrl("Facebook"),
  instagramUrl: getAsharaSocialUrl("Instagram"),
  xUrl: getAsharaSocialUrl("X"),
  youtubeUrl: getAsharaSocialUrl("YouTube"),
  recipientEmail: "{{recipientEmail}}",
  unsubscribeUrl: "{{unsubscribeUrl}}",
};

const SOCIAL_ICON_URLS = {
  facebook: "https://ashara-web.s3.us-east-2.amazonaws.com/Admin/SocialNetwork/Facebook.png",
  instagram: "https://ashara-web.s3.us-east-2.amazonaws.com/Admin/SocialNetwork/Instagram.png",
  x: "https://ashara-web.s3.us-east-2.amazonaws.com/Admin/SocialNetwork/Twitter.png",
  youtube: "https://ashara-web.s3.us-east-2.amazonaws.com/Admin/SocialNetwork/Youtube.png",
};

const DRAFT_STORAGE_KEY = "newsletterDraft.v1";
const DRAFT_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

// ── Escapado y URLs permitidas ────────────────────────────────────────────────
// Escapa también las comillas para que el texto no pueda salirse de un atributo.
const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const LINK_PROTOCOLS = ["http:", "https:", "mailto:"];
const IMAGE_PROTOCOLS = ["http:", "https:"];

// ── Inline markdown processor ──────────────────────────────────────────────────
function renderInline(raw) {
  return escapeHtml(raw)
    .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/~~(.+?)~~/g, "<del>$1</del>")
    .replace(
      /`([^`]+)`/g,
      '<code style="background:rgba(0,224,255,0.1);padding:2px 6px;border-radius:3px;font-family:monospace;font-size:0.88em;color:#a8d8f0;">$1</code>'
    )
    // Un enlace con protocolo no permitido (p. ej. `javascript:`) se deja como texto.
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) =>
      isSafeUrl(url, LINK_PROTOCOLS)
        ? `<a href="${url.trim()}" style="color:#00e0ff;text-decoration:none;font-weight:bold;" target="_blank">${text}</a>`
        : text
    );
}

// ── Markdown → email-safe HTML ────────────────────────────────────────────────
function parseMarkdown(text) {
  if (!text || !text.trim()) return "";

  const lines = text.split("\n");
  const output = [];
  let i = 0;
  let listStack = [];

  const closeAllLists = () => {
    while (listStack.length > 0) {
      output.push(listStack.pop() === "ul" ? "</ul>" : "</ol>");
    }
  };

  const pStyle =
    'margin:0 0 14px;color:#d0d0ec;font-size:15px;line-height:1.85;font-family:Arial,Helvetica,sans-serif;';

  while (i < lines.length) {
    const rawLine = lines[i];
    const line = rawLine.trim();

    // Code block
    if (line.startsWith("```")) {
      closeAllLists();
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        codeLines.push(
          lines[i].replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        );
        i++;
      }
      output.push(
        `<pre style="background:rgba(0,224,255,0.04);border:1px solid rgba(0,224,255,0.12);border-radius:6px;padding:14px 16px;margin:14px 0;overflow-x:auto;"><code style="font-family:'Courier New',monospace;font-size:13px;color:#a8d8f0;line-height:1.6;">${codeLines.join(
          "\n"
        )}</code></pre>`
      );
      i++;
      continue;
    }

    // Alignment block
    const alignBlockStart = line.match(/^:::(left|center|right|justify)$/i);
    if (alignBlockStart) {
      closeAllLists();
      const alignment = alignBlockStart[1].toLowerCase();
      const contentLines = [];
      i++;

      while (i < lines.length && lines[i].trim() !== ":::") {
        contentLines.push(lines[i]);
        i++;
      }

      const blockHtml = parseMarkdown(contentLines.join("\n"));
      output.push(
        `<div style="text-align:${alignment};">${blockHtml}</div>`
      );

      if (i < lines.length && lines[i].trim() === ":::") {
        i++;
      }
      continue;
    }

    // Empty line
    if (line === "") {
      closeAllLists();
      i++;
      continue;
    }

    // H3
    const h3 = line.match(/^### (.+)$/);
    if (h3) {
      closeAllLists();
      output.push(
        `<h3 style="color:rgba(0,224,255,0.8);font-size:15px;font-family:Arial,sans-serif;letter-spacing:1.5px;text-transform:uppercase;margin:22px 0 8px;">${renderInline(h3[1])}</h3>`
      );
      i++;
      continue;
    }

    // H2
    const h2 = line.match(/^## (.+)$/);
    if (h2) {
      closeAllLists();
      output.push(
        `<h2 style="color:#00e0ff;font-size:19px;font-family:Arial,sans-serif;letter-spacing:1.5px;text-transform:uppercase;margin:28px 0 12px;padding-bottom:8px;border-bottom:1px solid rgba(0,224,255,0.18);">${renderInline(h2[1])}</h2>`
      );
      i++;
      continue;
    }

    // H1
    const h1 = line.match(/^# (.+)$/);
    if (h1) {
      closeAllLists();
      output.push(
        `<h1 style="color:#00e0ff;font-size:26px;font-family:Arial,sans-serif;letter-spacing:2px;text-transform:uppercase;margin:32px 0 16px;">${renderInline(h1[1])}</h1>`
      );
      i++;
      continue;
    }

    // HR
    if (line.match(/^-{3,}$/) || line.match(/^\*{3,}$/) || line.match(/^_{3,}$/)) {
      closeAllLists();
      output.push(
        `<hr style="border:none;border-top:1px solid rgba(0,224,255,0.18);margin:20px 0;">`
      );
      i++;
      continue;
    }

    // Blockquote
    if (line.startsWith("> ")) {
      closeAllLists();
      output.push(
        `<blockquote style="border-left:3px solid rgba(0,224,255,0.45);margin:14px 0;padding:8px 16px;background:rgba(0,224,255,0.04);border-radius:0 4px 4px 0;"><p style="margin:0;color:#a0a8c8;font-style:italic;font-size:14px;line-height:1.7;font-family:Arial,sans-serif;">${renderInline(line.slice(2))}</p></blockquote>`
      );
      i++;
      continue;
    }

    // Standalone image: ![alt|opts](url)
    const imgStandalone = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgStandalone && isSafeUrl(imgStandalone[2], IMAGE_PROTOCOLS)) {
      closeAllLists();
      const [, altRaw, url] = imgStandalone;
      const [altText = "", optsStr = ""] = altRaw.split("|");
      const opts = optsStr.trim().toLowerCase();
      const sizeMatch = opts.match(/(\d+)%/);
      const width = sizeMatch ? sizeMatch[1] + "%" : "100%";
      const align = opts.includes("left") ? "left" : opts.includes("right") ? "right" : "center";
      output.push(
        `<div style="text-align:${align};margin:16px 0;"><img src="${escapeHtml(url.trim())}" alt="${escapeHtml(altText.trim())}" style="max-width:${width};height:auto;border-radius:6px;display:inline-block;"></div>`
      );
      i++;
      continue;
    }

    // Unordered list
    if (line.startsWith("* ") || line.startsWith("- ")) {
      if (listStack.length === 0 || listStack[listStack.length - 1] !== "ul") {
        closeAllLists();
        output.push(
          `<ul style="margin:10px 0;padding-left:22px;color:#d0d0ec;font-size:15px;line-height:1.85;font-family:Arial,Helvetica,sans-serif;">`
        );
        listStack.push("ul");
      }
      output.push(`<li style="margin-bottom:5px;">${renderInline(line.slice(2))}</li>`);
      i++;
      continue;
    }

    // Ordered list
    const olMatch = line.match(/^\d+\. (.+)$/);
    if (olMatch) {
      if (listStack.length === 0 || listStack[listStack.length - 1] !== "ol") {
        closeAllLists();
        output.push(
          `<ol style="margin:10px 0;padding-left:22px;color:#d0d0ec;font-size:15px;line-height:1.85;font-family:Arial,Helvetica,sans-serif;">`
        );
        listStack.push("ol");
      }
      output.push(`<li style="margin-bottom:5px;">${renderInline(olMatch[1])}</li>`);
      i++;
      continue;
    }

    // Paragraph
    closeAllLists();
    const rendered = renderInline(rawLine).replace(
      /!\[([^\]]*)\]\(([^)]+)\)/g,
      (match, altRaw, url) => {
        if (!isSafeUrl(url, IMAGE_PROTOCOLS)) return match;
        const [altText = "", optsStr = ""] = altRaw.split("|");
        const sizeMatch = optsStr.match(/(\d+)%/);
        const maxW = sizeMatch ? sizeMatch[1] + "%" : "auto";
        return `<img src="${url.trim()}" alt="${altText.trim()}" style="max-width:${maxW};height:auto;border-radius:4px;vertical-align:middle;margin:0 4px;">`;
      }
    );
    output.push(`<p style="${pStyle}">${rendered}</p>`);
    i++;
  }

  closeAllLists();
  return output.join("\n");
}

// ── Full email HTML wrapper ────────────────────────────────────────────────────
function buildEmailHtml(markdown, settings, subject, options = {}) {
  const { includeFooter = true, bodyHtmlOverride } = options;
  const {
    facebookUrl,
    instagramUrl,
    xUrl,
    youtubeUrl,
    unsubscribeUrl,
    recipientEmail,
  } = settings;

  const parsedBodyHtml = parseMarkdown(markdown);
  const bodyHtml = bodyHtmlOverride ?? parsedBodyHtml;

  const socials = [
    { name: "YouTube", href: youtubeUrl || "#", icon: SOCIAL_ICON_URLS.youtube },
    { name: "Instagram", href: instagramUrl || "#", icon: SOCIAL_ICON_URLS.instagram },
    { name: "X", href: xUrl || "#", icon: SOCIAL_ICON_URLS.x },
    { name: "Facebook", href: facebookUrl || "#", icon: SOCIAL_ICON_URLS.facebook },
  ];

  const footerHtml = includeFooter
    ? `
  <tr><td style="height:1px;background:#0c0c28;border-top:1px solid rgba(0,224,255,0.12);font-size:0;line-height:0;">&nbsp;</td></tr>

  <tr>
    <td align="center" style="background:#08081e;padding:24px 20px 14px;">
      <table cellpadding="0" cellspacing="0" border="0"><tr>
        ${socials
          .map(
            (s) =>
              `<td style="padding:0 8px;"><a href="${s.href}" target="_blank" style="text-decoration:none;display:inline-block;"><img class="social-icon" src="${s.icon}" alt="${s.name}" width="24" height="24" style="display:block;width:auto;height:24px;border:0;filter:none !important;mix-blend-mode:normal !important;forced-color-adjust:none !important;"></a></td>`
          )
          .join("")}
      </tr></table>
    </td>
  </tr>

  <tr>
    <td align="center" style="background:#08081e;border-radius:0 0 12px 12px;padding:6px 36px 26px;">
      <p style="margin:4px 0;color:#666;font-size:12px;line-height:1.8;font-family:Arial,sans-serif;">This email was sent to <strong style="color:#888;">${recipientEmail || "[email del destinatario]"}</strong> by <strong style="color:#888;">Ashara Studios</strong>.</p>
      <p style="margin:2px 0;color:#444;font-size:11px;font-family:Arial,sans-serif;">You received this email because you signed up on our website or made a purchase from us.</p>
      <p style="margin:8px 0 2px;font-size:11px;color:#444;font-family:Arial,sans-serif;">Not a fan? <a href="${unsubscribeUrl || "#"}" style="color:#00e0ff;text-decoration:none;">Unsubscribe</a></p>
      <p style="margin:12px 0 0;color:#666;font-size:11px;letter-spacing:3px;text-transform:uppercase;font-weight:bold;font-family:Arial,sans-serif;">Ashara Studios</p>
    </td>
  </tr>`
    : "";

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <meta name="color-scheme" content="light only">
  <meta name="supported-color-schemes" content="light">
  <title>${escapeHtml(subject || "Newsletter")}</title>
  <style>
    html, body {
      color-scheme: light !important;
    }

    .social-icon {
      filter: none !important;
      mix-blend-mode: normal !important;
      color-adjust: exact !important;
      -webkit-print-color-adjust: exact !important;
      forced-color-adjust: none !important;
    }
  </style>
</head>
<body style="margin:0;padding:24px 12px;background:#07071a;font-family:Arial,Helvetica,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td align="center">
<table width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;">

  <tr>
    <td style="background:#101038;border-radius:12px 12px 0 0;padding:32px 40px 24px;">
      ${bodyHtml || '<p style="color:#d0d0ec;font-size:15px;font-family:Arial,sans-serif;">&nbsp;</p>'}
    </td>
  </tr>
${footerHtml}

</table>
</td></tr></table>
</body></html>`;
}

// ── Insert markdown at textarea cursor ───────────────────────────────────────
function applyMarkdown(taRef, setter, before, after = "", defaultText = "") {
  const ta = taRef.current;
  if (!ta) return;
  const start = ta.selectionStart;
  const end = ta.selectionEnd;
  const selected = ta.value.substring(start, end) || defaultText;
  const newValue =
    ta.value.substring(0, start) + before + selected + after + ta.value.substring(end);
  setter(newValue);
  requestAnimationFrame(() => {
    ta.focus();
    ta.setSelectionRange(start + before.length, start + before.length + selected.length);
  });
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function EmailBuilder() {
  const dispatch = useDispatch();
  const { sendStatus, testStatus, topics, topicsStatus } = useSelector((state) => state.newsletter);

  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("english");
  const [subject, setSubject] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [copied, setCopied] = useState(false);
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);
  const [isPreviewLoaded, setIsPreviewLoaded] = useState(false);
  // Popup activo: { type: "link" | "image" | "discard" | "notice", ...campos }.
  const [dialog, setDialog] = useState(null);

  const taRef = useRef(null);
  const previewIframeRef = useRef(null);
  const apply = (before, after = "", defaultText = "") =>
    applyMarkdown(taRef, setMarkdown, before, after, defaultText);

  const previewShellHtml = useMemo(
    () =>
      buildEmailHtml("", FOOTER_SETTINGS, undefined, {
        includeFooter: true,
        bodyHtmlOverride:
          '<div id="preview-email-body"><p style="color:#d0d0ec;font-size:15px;font-family:Arial,sans-serif;">&nbsp;</p></div>',
      }),
    []
  );

  const previewBodyHtml = useMemo(
    () =>
      parseMarkdown(markdown) ||
      '<p style="color:#d0d0ec;font-size:15px;font-family:Arial,sans-serif;">&nbsp;</p>',
    [markdown]
  );

  useEffect(() => {
    dispatch(fetchTopics());
  }, [dispatch]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!saved) {
        setIsDraftLoaded(true);
        return;
      }

      const parsed = JSON.parse(saved);
      const updatedAt = Number(parsed?.updatedAt || 0);
      const isExpired = !updatedAt || Date.now() - updatedAt > DRAFT_EXPIRY_MS;

      if (isExpired) {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        setIsDraftLoaded(true);
        return;
      }

      setSubject(parsed?.subject || "");
      setMarkdown(parsed?.markdown || "");
    } catch {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    } finally {
      setIsDraftLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!isDraftLoaded) return;

    const timeoutId = setTimeout(() => {
      const hasContent = Boolean(subject || markdown);
      if (!hasContent) {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        return;
      }

      localStorage.setItem(
        DRAFT_STORAGE_KEY,
        JSON.stringify({
          subject,
          markdown,
          updatedAt: Date.now(),
        })
      );
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [subject, markdown, isDraftLoaded]);

  useEffect(() => {
    if (!isPreviewLoaded) return;

    const previewDoc = previewIframeRef.current?.contentDocument;
    const bodyContainer = previewDoc?.getElementById("preview-email-body");
    if (!bodyContainer) return;

    bodyContainer.innerHTML = previewBodyHtml;
  }, [isPreviewLoaded, previewBodyHtml]);

  const updateDialog = (key, value) => setDialog((prev) => ({ ...prev, [key]: value }));
  const closeDialog = () => setDialog(null);
  const showNotice = (title, message) => setDialog({ type: "notice", title, message });
  const submitOnEnter = (event, action) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    action();
  };

  // El textarea conserva la selección mientras el popup está abierto.
  const openLinkDialog = () => {
    const ta = taRef.current;
    if (!ta) return;
    const selected = ta.value.substring(ta.selectionStart, ta.selectionEnd);
    setDialog({ type: "link", url: "", text: selected || "enlace" });
  };

  const insertLink = () => {
    const ta = taRef.current;
    const url = dialog.url.trim();
    if (!ta || !url) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const replacement = `[${dialog.text.trim() || "enlace"}](${url})`;
    const newValue = ta.value.substring(0, start) + replacement + ta.value.substring(end);
    setMarkdown(newValue);
    closeDialog();
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + replacement.length, start + replacement.length);
    });
  };

  const insertImage = () => {
    const url = dialog.url.trim();
    if (!url) return;
    const alt = dialog.alt.trim() || "imagen";
    const size = dialog.size.trim();
    const altStr = size ? `${alt}|${size}` : alt;
    closeDialog();
    apply(`\n![${altStr}](${url})\n`);
  };

  const copyHtml = async () => {
    const html = buildEmailHtml(markdown, FOOTER_SETTINGS, subject);
    try {
      await navigator.clipboard.writeText(html);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = html;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleDiscard = () => {
    setMarkdown("");
    setSubject("");
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    closeDialog();
  };

  const handleSend = async () => {
    // El botón ya está deshabilitado sin tópico; esto solo evita un envío accidental.
    if (!selectedTopic) return;

    const body = buildEmailHtml(markdown, FOOTER_SETTINGS, subject);
    const result = await dispatch(
      sendNewsletter({ topic: selectedTopic, subject, body, language: selectedLanguage })
    );

    if (sendNewsletter.fulfilled.match(result)) {
      showNotice("Campaña creada", result.payload?.message || "El envío está en curso.");
      setSubject("");
      setMarkdown("");
      setSelectedTopic("");
      setSelectedLanguage("english");
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      dispatch(resetSendStatus());
    } else {
      showNotice("Error al enviar", result.payload ?? "Error desconocido");
      dispatch(resetSendStatus());
    }
  };

  // Envía el correo solo a tu correo interno para revisarlo antes del envío real.
  const handleSendTest = async () => {
    const body = buildEmailHtml(markdown, FOOTER_SETTINGS, subject);
    const result = await dispatch(
      sendTestNewsletter({ topic: selectedTopic, subject, body, language: selectedLanguage })
    );

    if (sendTestNewsletter.fulfilled.match(result)) {
      showNotice("Correo de prueba enviado", `Se envió${result.payload?.to ? ` a ${result.payload.to}` : " a tu correo interno"}.`);
    } else {
      showNotice("Error al enviar la prueba", result.payload ?? "Error desconocido");
    }
    dispatch(resetSendStatus());
  };

  const isMissingFields = !selectedTopic || !selectedLanguage || !subject.trim() || !markdown.trim();

  return (
    <div className="email-builder">

      {/* ── Header Row: Subject + Topic + Language ── */}
      <div className="eb-filters">
        <div className="eb-filter-field eb-filter-field--subject">
          <span className="eb-filter-label">Asunto</span>
          <input
            className="eb-filter-input"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Asunto del correo"
          />
        </div>

        <div className="eb-filter-field">
          <span className="eb-filter-label">Tópico</span>
          <select
            className="eb-filter-select"
            value={selectedTopic}
            onChange={(e) => setSelectedTopic(e.target.value)}
          >
            <option value="">Seleccionar tópico...</option>
            {topicsStatus === "loading" && (
              <option disabled>Cargando...</option>
            )}
            {topics.map((topic) => (
              <option key={topic.key} value={topic.key}>
                {topic.label}
              </option>
            ))}
          </select>
        </div>

        <div className="eb-filter-field">
          <span className="eb-filter-label">Idioma</span>
          <select
            className="eb-filter-select"
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
          >
            <option value="spanish">Español</option>
            <option value="english">English</option>
          </select>
        </div>

        <div className="eb-actions-col">
          <div className="eb-actions">
            <Button type="button" variant="negative" size="small" className="eb-action-btn" onClick={() => setDialog({ type: "discard" })}>
              <FontAwesomeIcon icon={faTrash} /> Descartar
            </Button>
            <Button type="button" variant="outline" size="small" className="eb-action-btn" onClick={handleSendTest} disabled={testStatus === "loading" || sendStatus === "loading" || isMissingFields} title="Envía el correo solo a tu correo interno">
              <FontAwesomeIcon icon={faVial} /> {testStatus === "loading" ? "Enviando…" : "Probar"}
            </Button>
            <Button type="button" variant="primary" size="small" className="eb-action-btn" onClick={handleSend} disabled={sendStatus === "loading" || testStatus === "loading" || isMissingFields}>
              <FontAwesomeIcon icon={faPaperPlane} /> {sendStatus === "loading" ? "Enviando…" : "Enviar"}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Body: Editor + Preview ── */}
      <div className="eb-body">

        {/* Left: Markdown editor */}
        <div className="eb-editor-col">
          <div className="eb-toolbar">
            <button type="button" className="eb-tb-btn eb-tb-text" title="Título H1" onClick={() => apply("\n# ", "", "Título")}>H1</button>
            <button type="button" className="eb-tb-btn eb-tb-text" title="Subtítulo H2" onClick={() => apply("\n## ", "", "Subtítulo")}>H2</button>
            <button type="button" className="eb-tb-btn eb-tb-text" title="Sección H3" onClick={() => apply("\n### ", "", "Sección")}>H3</button>
            <span className="eb-tb-sep" />
            <button type="button" className="eb-tb-btn" title="Alinear izquierda" onClick={() => apply("\n:::left\n", "\n:::\n", "texto alineado") }><FontAwesomeIcon icon={faAlignLeft} /></button>
            <button type="button" className="eb-tb-btn" title="Centrar" onClick={() => apply("\n:::center\n", "\n:::\n", "texto centrado") }><FontAwesomeIcon icon={faAlignCenter} /></button>
            <button type="button" className="eb-tb-btn" title="Alinear derecha" onClick={() => apply("\n:::right\n", "\n:::\n", "texto alineado") }><FontAwesomeIcon icon={faAlignRight} /></button>
            <button type="button" className="eb-tb-btn" title="Justificar" onClick={() => apply("\n:::justify\n", "\n:::\n", "texto justificado") }><FontAwesomeIcon icon={faAlignJustify} /></button>
            <span className="eb-tb-sep" />
            <button type="button" className="eb-tb-btn" title="Negrita" onClick={() => apply("**", "**", "texto")}><FontAwesomeIcon icon={faBold} /></button>
            <button type="button" className="eb-tb-btn" title="Cursiva" onClick={() => apply("*", "*", "texto")}><FontAwesomeIcon icon={faItalic} /></button>
            <button type="button" className="eb-tb-btn" title="Tachado" onClick={() => apply("~~", "~~", "texto")}><FontAwesomeIcon icon={faStrikethrough} /></button>
            <span className="eb-tb-sep" />
            <button type="button" className="eb-tb-btn" title="Insertar enlace" onClick={openLinkDialog}><FontAwesomeIcon icon={faLink} /></button>
            <button type="button" className="eb-tb-btn" title="Insertar imagen" onClick={() => setDialog({ type: "image", url: "", alt: "imagen", size: "" })}><FontAwesomeIcon icon={faImage} /></button>
            <span className="eb-tb-sep" />
            <button type="button" className="eb-tb-btn" title="Lista sin orden" onClick={() => apply("\n* ", "", "Item")}><FontAwesomeIcon icon={faListUl} /></button>
            <button type="button" className="eb-tb-btn" title="Lista numerada" onClick={() => apply("\n1. ", "", "Item")}><FontAwesomeIcon icon={faListOl} /></button>
            <button type="button" className="eb-tb-btn" title="Cita" onClick={() => apply("\n> ", "", "Cita")}><FontAwesomeIcon icon={faQuoteLeft} /></button>
            <button type="button" className="eb-tb-btn" title="Código" onClick={() => apply("`", "`", "código")}><FontAwesomeIcon icon={faCode} /></button>
            <button type="button" className="eb-tb-btn" title="Línea separadora" onClick={() => apply("\n\n---\n\n")}><FontAwesomeIcon icon={faMinus} /></button>
          </div>

          <textarea
            ref={taRef}
            className="eb-textarea"
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            placeholder={"# Título principal\n\nEscribe el contenido con **negrilla**, *cursiva* o ~~tachado~~.\n\n![imagen|50%](https://url.com/imagen.png)\n\n## Subtítulo\n\n* Item de lista\n* Otro item\n\n[Ver más](https://asharastudios.com)"}
            spellCheck={false}
          />
        </div>

        {/* Right: Preview */}
        <div className="eb-preview-col">
          <div className="eb-preview-bar">
            <span className="eb-preview-title">Vista previa</span>
            <button
              type="button"
              className={`eb-copy-btn${copied ? " copied" : ""}`}
              onClick={copyHtml}
            >
              {copied ? "✓ HTML copiado" : "Copiar HTML"}
            </button>
          </div>
          <iframe
            ref={previewIframeRef}
            className="eb-iframe"
            srcDoc={previewShellHtml}
            title="Vista previa del email"
            sandbox="allow-same-origin"
            onLoad={() => setIsPreviewLoaded(true)}
          />
        </div>

      </div>

      <Popup
        isOpen={dialog?.type === "link"}
        title="Insertar enlace"
        onClose={closeDialog}
        onConfirm={insertLink}
        confirmText="Insertar"
        confirmDisabled={!dialog?.url?.trim()}
      >
        {dialog?.type === "link" && (
          <div className="eb-dialog-form" onKeyDown={(e) => submitOnEnter(e, insertLink)}>
            <label className="eb-filter-field">
              <span className="eb-filter-label">URL del enlace</span>
              <input className="eb-filter-input" type="url" value={dialog.url} onChange={(e) => updateDialog("url", e.target.value)} placeholder="https://..." autoFocus />
            </label>
            <label className="eb-filter-field">
              <span className="eb-filter-label">Texto visible</span>
              <input className="eb-filter-input" type="text" value={dialog.text} onChange={(e) => updateDialog("text", e.target.value)} />
            </label>
          </div>
        )}
      </Popup>

      <Popup
        isOpen={dialog?.type === "image"}
        title="Insertar imagen"
        onClose={closeDialog}
        onConfirm={insertImage}
        confirmText="Insertar"
        confirmDisabled={!dialog?.url?.trim()}
      >
        {dialog?.type === "image" && (
          <div className="eb-dialog-form" onKeyDown={(e) => submitOnEnter(e, insertImage)}>
            <label className="eb-filter-field">
              <span className="eb-filter-label">URL de la imagen</span>
              <input className="eb-filter-input" type="url" value={dialog.url} onChange={(e) => updateDialog("url", e.target.value)} placeholder="https://..." autoFocus />
            </label>
            <label className="eb-filter-field">
              <span className="eb-filter-label">Texto alternativo (opcional)</span>
              <input className="eb-filter-input" type="text" value={dialog.alt} onChange={(e) => updateDialog("alt", e.target.value)} />
            </label>
            <label className="eb-filter-field">
              <span className="eb-filter-label">Tamaño (ej: 50%) — vacío para ancho completo</span>
              <input className="eb-filter-input" type="text" value={dialog.size} onChange={(e) => updateDialog("size", e.target.value)} placeholder="100%" />
            </label>
          </div>
        )}
      </Popup>

      <Popup
        isOpen={dialog?.type === "discard"}
        title="Descartar correo"
        onClose={closeDialog}
        onConfirm={handleDiscard}
        confirmText="Descartar"
      >
        <p className="eb-dialog-text">¿Descartar todo el contenido del correo?</p>
      </Popup>

      <Popup
        isOpen={dialog?.type === "notice"}
        title={dialog?.title}
        onClose={closeDialog}
        onConfirm={closeDialog}
        confirmText="Aceptar"
        showCancel={false}
      >
        <p className="eb-dialog-text">{dialog?.message}</p>
      </Popup>

    </div>
  );
}
